import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Container,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Security,
  VerifiedUser,
  AdminPanelSettings,
  CloudUpload,
  Fingerprint,
} from '@mui/icons-material';

const HomePage: React.FC = () => {
  const features = [
    {
      icon: <Fingerprint sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'NFT Identity Minting',
      description: 'Mint your unique identity NFT - one wallet, one identity. Free minting with complete ownership and control over your digital identity.',
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Multi-Template KYC',
      description: 'Flexible KYC verification system supporting personal, enterprise, and high-net-worth verification templates with layered authentication.',
    },
    {
      icon: <AdminPanelSettings sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Smart Permission Management',
      description: 'Role-based access control with fine-grained permissions. From super admin to read-only access with whitelist mechanisms.',
    },
    {
      icon: <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'IPFS Decentralized Storage',
      description: 'Your identity data is securely stored on IPFS with encryption, ensuring privacy protection and data sovereignty.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          mb: 6,
          borderRadius: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            DecentralizedKYC Platform
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{ textAlign: 'center', mb: 2, opacity: 0.9 }}
          >
            Revolutionary Decentralized Identity Verification System
          </Typography>
          <Typography
            variant="body1"
            component="p"
            sx={{ textAlign: 'center', mb: 4, opacity: 0.8, maxWidth: '600px', mx: 'auto' }}
          >
            Own and control your identity data with blockchain-powered NFT identities. 
            One verification, multiple platforms - experience true user sovereignty.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/assets"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Identity List
            </Button>
            <Button
              component={Link}
              to="/mint"
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Mint Identity NFT
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          sx={{ textAlign: 'center', mb: 6, fontWeight: 'bold' }}
        >
          Core Features
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Value Proposition */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Start Your Decentralized Identity Journey
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Connect your wallet and take control of your digital identity with blockchain-powered verification
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/assets"
              variant="contained"
              size="large"
            >
              Identity List
            </Button>
            <Button
              component={Link}
              to="/mint"
              variant="outlined"
              size="large"
            >
              Mint Identity NFT
            </Button>
            {/* <Button
              component={Link}
              to="/admin"
              variant="text"
              size="large"
            >
              Admin Panel
            </Button> */}
          </Box>
        </Box>

        {/* Core Values */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}
          >
            Core Values
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                  User Sovereignty
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete ownership and control of your identity data
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                  Privacy Protection
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  IPFS encrypted storage protects sensitive information
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                  Interoperability
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One verification, multiple platform usage
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                  Compliance & Transparency
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Meet regulatory requirements while maintaining transparency
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;